import React from 'react';
import { Button, Typography } from 'antd';

const { Text } = Typography;

/**
 * EffectsPanel — A floating panel listing available image effects.
 * Clicking an effect opens its dedicated editor.
 *
 * Props:
 *  - visible: boolean
 *  - onClose: () => void
 *  - onSelectEffect: (effectId: string) => void
 *  - hasSelectedLayer: boolean
 */

const EFFECTS = [
    { id: 'halftone', icon: '◉', name: 'Halftone', desc: 'CMYK dot pattern printing' },
    { id: 'dither', icon: '▦', name: 'Dither', desc: 'Retro dithering patterns' },
    { id: 'gradient-map', icon: '▰', name: 'Gradient Map', desc: 'Map luminosity to colors' },
    { id: 'chromatic', icon: '◎', name: 'Chromatic Aberration', desc: 'RGB channel shift' },
    { id: 'grain', icon: '⣿', name: 'Grain', desc: 'Film grain texture' },
    { id: 'glitch', icon: '⚡', name: 'Glitch', desc: 'Digital corruption & distortion' },
    { id: 'ascii', icon: 'A_', name: 'ASCII Art', desc: 'Text character rendering' },
    { id: 'pattern', icon: '⬡', name: 'Pattern', desc: 'Parametric geometric patterns' },
    { id: 'topographic', icon: '⛰', name: 'Topographic', desc: 'Contour line terrain patterns' },
];

const EffectsPanel = ({ visible, onClose, onSelectEffect, hasSelectedLayer }) => {
    if (!visible) return null;

    return (
        <div
            style={{
                position: 'absolute',
                top: 48,
                right: 10,
                width: 240,
                zIndex: 90,
                background: 'rgba(13, 17, 23, 0.97)',
                border: '1px solid #30363d',
                borderRadius: 10,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                overflow: 'hidden',
                pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                borderBottom: '1px solid #30363d',
                background: '#161b22',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>✦</span>
                    <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 13 }}>Effects</span>
                </div>
                <Button
                    type="text"
                    size="small"
                    onClick={onClose}
                    style={{ color: '#8b949e', fontSize: 11, padding: '2px 6px' }}
                >
                    ✕
                </Button>
            </div>

            {/* Effects List */}
            <div style={{ padding: 6 }}>
                {!hasSelectedLayer && (
                    <div style={{
                        padding: '10px 12px',
                        color: '#8b949e',
                        fontSize: 11,
                        textAlign: 'center',
                        fontStyle: 'italic',
                    }}>
                        Select a layer to apply effects
                    </div>
                )}
                {EFFECTS.map((effect) => (
                    <div
                        key={effect.id}
                        onClick={() => {
                            if (hasSelectedLayer) onSelectEffect(effect.id);
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '8px 10px',
                            borderRadius: 6,
                            cursor: hasSelectedLayer ? 'pointer' : 'not-allowed',
                            opacity: hasSelectedLayer ? 1 : 0.4,
                            transition: 'all 0.15s ease',
                            background: 'transparent',
                        }}
                        onMouseEnter={(e) => {
                            if (hasSelectedLayer) e.currentTarget.style.background = '#21262d';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                        }}
                    >
                        <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: 6,
                            background: '#21262d',
                            border: '1px solid #30363d',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 16,
                            flexShrink: 0,
                        }}>
                            {effect.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 500 }}>
                                {effect.name}
                            </div>
                            <div style={{ color: '#6e7681', fontSize: 10, marginTop: 1 }}>
                                {effect.desc}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default EffectsPanel;
